import { useEffect, useRef, useState } from 'react';

let loadPromise = null;

function loadCaptchaScriptOnce(captchaScriptUrl) {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('no window'));

    const alreadyRegistered =
      (window.customElements && typeof window.customElements.get === 'function' && window.customElements.get('awswaf-captcha')) ||
      !!window.AwsWafCaptcha;

    if (alreadyRegistered) return resolve();

    const existing = document.querySelector(`script[src="${captchaScriptUrl}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', (e) => reject(e), { once: true });
      return;
    }

    const s = document.createElement('script');
    s.src = captchaScriptUrl;
    s.defer = true;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = (e) => reject(e);
    document.body.appendChild(s);
  });

  return loadPromise;
}

export default function useCaptcha({ captchaScriptUrl, encryptedApiKey }) {
  const captchaRef = useRef(null);
  const isCaptchaRenderedRef = useRef(false);

  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  /**
   * renderCaptcha(onSuccess, onError)
   * - onSuccess(wafToken)
   * - onError(message)
   *
   * NOTE: onError is forwarded to the caller but we do NOT auto-hide the captcha
   * so the user can retry. Fatal load/render failures will still hide.
   */
  async function renderCaptcha(onSuccess, onError) {
    if (isCaptchaRenderedRef.current) return;
    if (!captchaRef.current) {
      console.warn('[captcha] container missing');
      if (typeof onError === 'function') onError('CAPTCHA container is missing.');
      return;
    }

    try {
      await loadCaptchaScriptOnce(captchaScriptUrl);
    } catch (err) {
      console.error('[captcha] loadCaptchaScript error', err);
      setShowCaptcha(false);
      if (typeof onError === 'function') {
        onError('Failed to load CAPTCHA. Please try again later.  Contact Administrator if problem persists.');
      }
      return;
    }

    if (typeof window === 'undefined' || !window.AwsWafCaptcha) {
      const hasCE =
        typeof window !== 'undefined' &&
        window.customElements &&
        typeof window.customElements.get === 'function' &&
        window.customElements.get('awswaf-captcha');

      if (!hasCE) {
        console.error('[captcha] AwsWafCaptcha / custom element missing after load');
        setShowCaptcha(false);
        if (typeof onError === 'function') {
          onError('CAPTCHA is unavailable.');
        }
        return;
      }
    }

    try {
      isCaptchaRenderedRef.current = true;

      window.AwsWafCaptcha.renderCaptcha(captchaRef.current, {
        apiKey: encryptedApiKey,
        onSuccess: (wafToken) => {
          setCaptchaToken(wafToken);
          // Hide the container via state — do NOT clear innerHTML here.
          // The SDK stores its internal state on the DOM node itself; removing
          // child nodes (innerHTML = '') while the SDK's async /report fetch is
          // still in-flight causes "Cannot read properties of undefined
          // (reading 'submitCaptcha')", which then fires onError.
          // React will unmount the node naturally once showCaptcha is false.
          setShowCaptcha(false);
          isCaptchaRenderedRef.current = false;
          if (typeof onSuccess === 'function') onSuccess(wafToken);
        },
        onError: (err) => {
          // IMPORTANT: do NOT hide the captcha on vendor errors so user can retry.
          console.error('[captcha] onError', err);
          if (typeof onError === 'function') {
            // forward friendly message to the consumer to display inline
            onError('CAPTCHA verification failed — please try again.  Contact Administrator if problem persists.');
          }
          // keep the captcha rendered (allow retry)
        },
        dynamicWidth: true,
        skipTitle: true,
      });

      console.debug('[captcha] rendered');
    } catch (err) {
      console.error('[captcha] render failed', err);
      // render failed — consider this fatal for now and hide
      setShowCaptcha(false);
      isCaptchaRenderedRef.current = false;
      if (typeof onError === 'function') {
        onError('Failed to render CAPTCHA.');
      }
    }
  }

  function cleanupCaptcha() {
    try {
      // Do NOT clear innerHTML — the SDK may still be running async work
      // (e.g. /report fetch) against the DOM node. Let React unmount it.
      isCaptchaRenderedRef.current = false;
      setCaptchaToken(null);
      setShowCaptcha(false);
    } catch (e) {
      console.warn('[captcha] cleanup error', e);
    }
  }

  useEffect(() => {
    return () => {
      cleanupCaptcha();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    captchaRef,
    showCaptcha,
    setShowCaptcha,
    renderCaptcha,
    cleanupCaptcha,
    captchaToken,
  };
}
